/**
 * API Interceptors Module - Request/Response Interceptors for ArbiWin
 *
 * This module provides centralized request and response interceptors
 * for the ArbiWin API client, handling authentication, error processing,
 * and response transformation.
 *
 * Following constitution requirements for API integration
 * and modular architecture.
 */

// Request Interceptor
export {
  createRequestInterceptor,
  createRequestErrorHandler,
  TokenManager,
  TokenRefreshManager
} from './RequestInterceptor';

// Response Interceptor
export {
  createResponseInterceptor,
  createResponseErrorHandler,
  ResponsePerformanceTracker,
  ErrorResponseHandler
} from './ResponseInterceptor';

/**
 * Interceptors Module Information
 */
export const INTERCEPTORS_MODULE_INFO = {
  name: 'ArbiWin API Interceptors',
  version: '1.0.0',
  description: 'Request and response interceptors for JWT token management and error handling',
  features: [
    'JWT token injection and refresh',
    'Automatic token cleanup on 401',
    'Subscription error handling (402)',
    'Response performance tracking',
    'Error standardization and recovery',
    'Security headers injection',
    'Request/response logging',
    'Rate limiting detection'
  ]
};

/**
 * Quick access to interceptor utilities
 */
export const interceptorUtils = {
  // Token management
  token: {
    get: () => {
      const { TokenManager } = require('./RequestInterceptor');
      return TokenManager.getAccessToken();
    },
    refresh: () => {
      const { TokenRefreshManager } = require('./RequestInterceptor');
      return TokenRefreshManager.refreshToken();
    },
    clear: () => {
      const { TokenManager } = require('./RequestInterceptor');
      return TokenManager.clearTokens();
    },
    isExpired: () => {
      const { TokenManager } = require('./RequestInterceptor');
      return TokenManager.isTokenExpired();
    }
  },

  // Performance monitoring
  performance: {
    getStats: () => {
      const { ResponsePerformanceTracker } = require('./ResponseInterceptor');
      return ResponsePerformanceTracker.getStats();
    },
    clearStats: () => {
      const { ResponsePerformanceTracker } = require('./ResponseInterceptor');
      return ResponsePerformanceTracker.responses.clear();
    }
  },

  // Error handling
  error: {
    handle: (error) => {
      const { ErrorResponseHandler } = require('./ResponseInterceptor');
      return ErrorResponseHandler.handleError(error);
    },
    standardize: (error, info) => {
      const { ErrorResponseHandler } = require('./ResponseInterceptor');
      return ErrorResponseHandler.createStandardizedError(error, info);
    }
  }
};

/**
 * Create complete interceptor setup for API client
 * @returns {Object} Interceptor configuration
 */
export const createInterceptorConfig = () => {
  const { createRequestInterceptor, createRequestErrorHandler } = require('./RequestInterceptor');
  const { createResponseInterceptor, createResponseErrorHandler } = require('./ResponseInterceptor');

  return {
    request: {
      onFulfilled: createRequestInterceptor(),
      onRejected: createRequestErrorHandler()
    },
    response: {
      onFulfilled: createResponseInterceptor(),
      onRejected: createResponseErrorHandler()
    }
  };
};

export default {
  createRequestInterceptor: () => {
    const { createRequestInterceptor } = require('./RequestInterceptor');
    return createRequestInterceptor();
  },
  createRequestErrorHandler: () => {
    const { createRequestErrorHandler } = require('./RequestInterceptor');
    return createRequestErrorHandler();
  },
  createResponseInterceptor: () => {
    const { createResponseInterceptor } = require('./ResponseInterceptor');
    return createResponseInterceptor();
  },
  createResponseErrorHandler: () => {
    const { createResponseErrorHandler } = require('./ResponseInterceptor');
    return createResponseErrorHandler();
  },
  createInterceptorConfig,
  interceptorUtils,
  INTERCEPTORS_MODULE_INFO
};
